// ActiveSubscriptionSummary.jsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { ModernButton } from "../components/DefaultImports";
import { useSelector, useDispatch } from "react-redux";
import { fetchSubscriptionPackages } from "../actions/userActions";
import { Card } from "react-bootstrap";
import { ConfirmationModal } from "./ConfirmationModal";
import { resumeSubscription, cancelSubscription } from "../actions/paymentActions";
import { fetchRestaurantDetails } from "../actions/restaurantActions";
import { SectionTitle } from "./DefaultImports";

/* Palette and helper for plan colors */
const palette = ["#9FBBC1", "#EE754C", "#99B0AD"];
const getPlanColor = n => palette[(Number(n) - 1) % palette.length];

/* ---------- Styled components ---------- */
const Wrapper = styled.div``;
const PlanName = styled.h4`
  font-size: 20px;
  font-weight: 700;
  color: ${({
  color
}) => color || "#333"};
  margin: 8px 0 6px;
`;
const PlanDescription = styled.p`
  font-size: 14px;
  color: #444;
  margin: 0 0 16px;
`;
const DiscountNote = styled.div`
  font-size: 13px;
  color: #28a745;
  font-style: italic;
  margin: 0 0 12px;
`;
const DowngradeWarning = styled.div`
  margin: 12px 0 18px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff3cd;
  border: 1px solid #ffeeba;
  color: #856404;
  font-size: 14px;
  line-height: 1.4;
  text-align: left;

  strong {
    color: #6f5100;
  }
`;
const DowngradeWarningTitle = styled.div`
  font-weight: 800;
  margin-bottom: 4px;
`;
const FeatureList = styled.ul`
  list-style: none;
  padding-left: 0;
  margin: 8px 0 14px;

  li {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #333;
    margin-bottom: 8px;
    font-family: "Kanit", sans-serif;

    svg {
      margin-right: 10px;
      font-size: 14px;
      color: ${({
  iconColor
}) => iconColor || "#ee754c"};
    }
  }
`;
const ViewMore = styled.button`
  background: none;
  border: none;
  color: ${p => p.color || "#ee754c"};
  cursor: pointer;
  font-size: 14px;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;
const Buttons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 10px;
`;
const EmptyTitle = styled.h4`
  font-size: 20px;
  font-weight: 800;
  color: #99b0ad;
  text-align: center;
  margin: 4px 0 6px;
`;
const EmptyText = styled.p`
  text-align: center;
  color: #6f7a79;
  margin: 0 0 10px;
`;
const PlanCard = styled(Card)`
  border-radius: 20px;
  border: 1px solid #dee2e6;
  background-color: #ffffff;
  padding: 32px 24px;
  text-align: center;
  transition: transform 0.3s ease;
  min-height: 100%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);

  &:hover {
    transform: translateY(-5px);
  }
`;

/* ---------- helpers ---------- */
const normalizeSubLevel = level => {
  const n = Number(level || 0);
  if (n === 4) return 1;
  if (n === 5) return 2;
  if (n === 6) return 3;
  return n;
};
const toDate = v => {
  if (!v) return null;

  // aceita Date, ISO string ou timestamp
  if (v instanceof Date) return v;
  const n = Number(v);
  if (Number.isFinite(n)) {
    return new Date(n < 1e12 ? n * 1000 : n); // sec ou ms
  }
  return new Date(v); // ISO do Django
};
const fmt = (d, locale) => d ? d.toLocaleDateString(locale, {
  year: "numeric",
  month: "long",
  day: "numeric"
}) : "—";

/**
 * Props:
 * - onChangePlan?: () => void
 * - onCancelPlan?: () => void
 * - onSubscribe?: () => void
 * - restaurant_id: string | number
 */
export default function ActiveSubscriptionSummary({
  onChangePlan,
  onCancelPlan,
  onSubscribe,
  restaurant_id
}) {
  const { t, i18n } = useTranslation();
  const appLocale = (i18n.resolvedLanguage || i18n.language || "en").startsWith("pt") ? "pt-PT" : (i18n.resolvedLanguage || i18n.language || "en").startsWith("es") ? "es-ES" : "en-GB";
  const dispatch = useDispatch();
  const params = useParams();
  const [showMore, setShowMore] = useState(false);
  const [subscription, setSubscription] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState(null); // 'cancel' | 'resume'
  const [working, setWorking] = useState(false);
  const restaurantDetails = useSelector(state => state.restaurantDetails);
  const {
    restaurants
  } = restaurantDetails;
  const {
    getSubscriptionPackages
  } = useSelector(state => state.subscriptionPackagesStore);
  useEffect(() => {
    dispatch(fetchSubscriptionPackages());
  }, [dispatch]);
  useEffect(() => {
    if (!getSubscriptionPackages || !restaurants) return;
    const sub_lvl = normalizeSubLevel(restaurants.sub_lvl);
    const match = getSubscriptionPackages.find(pkg => Number(pkg.subscription_number) === Number(sub_lvl));
    if (match) setSubscription(match);
  }, [getSubscriptionPackages, restaurants]);

  // Sem plano
  if (restaurants?.subscription_status === "incomplete" || restaurants?.subscription_status === "incomplete_expired") {
    return <Wrapper>
        <SectionTitle>{t("ui.components.activeSubscription.planoAtivo")}</SectionTitle>

        <EmptyTitle>{t("ui.components.activeSubscription.semPlanoAtivo")}</EmptyTitle>

        <EmptyText>{t("ui.components.activeSubscription.naoEncontramosNenhumPlanoAtivoNeste")}</EmptyText>

        <Buttons>
          <Link to={`/pick_plan_and_pay/${restaurant_id}`}>
            <ModernButton color="#de8c3a" fontSize="14px" onClick={onSubscribe}>{t("ui.components.activeSubscription.aderirAPlano")}</ModernButton>
          </Link>
        </Buttons>
      </Wrapper>;
  }

  /* ---- Estado + Datas ---- */
  const status = restaurants?.subscription_status; // 'active' | 'trialing' | 'paused' | ...
  const cancelAtPeriodEnd = !!restaurants?.cancel_at_period_end;
  const periodEnd = toDate(restaurants?.subscription_end_date || restaurants?.current_period_end || restaurants?.subscription_current_period_end);
  const periodEndLabel = fmt(periodEnd, appLocale);
  const downgradeAtPeriodEnd = normalizeSubLevel(restaurants?.downgrade_at_period_end);
  const hasScheduledDowngrade = downgradeAtPeriodEnd > 0;
  const downgradeTargetSubscription = hasScheduledDowngrade ? getSubscriptionPackages?.find(pkg => Number(pkg.subscription_number) === Number(downgradeAtPeriodEnd)) : null;
  const downgradeTargetName = downgradeTargetSubscription?.name || t("ui.i18nFinalCleanup.planNumber", { number: downgradeAtPeriodEnd });
  const isPaused = status === "paused" || cancelAtPeriodEnd === true;
  const color = getPlanColor(subscription.subscription_number);
  const isYearly = Number(subscription.number_of_days) === 365;
  const offers = subscription.offers ? String(subscription.offers).split(",").map(s => s.trim()).filter(Boolean) : [];

  // Ações
  const askCancel = () => {
    setConfirmType("cancel");
    setConfirmOpen(true);
  };
  const askResume = () => {
    setConfirmType("resume");
    setConfirmOpen(true);
  };
  const onConfirm = async () => {
    if (working) return;
    try {
      setWorking(true);
      if (confirmType === "cancel") {
        await dispatch(cancelSubscription({
          restaurant_id
        }));
        if (typeof onCancelPlan === "function") {
          onCancelPlan();
        }
      } else if (confirmType === "resume") {
        await dispatch(resumeSubscription({
          restaurant_id
        }));
        if (typeof onChangePlan === "function") {
          onChangePlan();
        }
      }
      await dispatch(fetchRestaurantDetails(restaurant_id));
    } finally {
      setWorking(false);
      setConfirmOpen(false);
      setConfirmType(null);
    }
  };
  return <Wrapper>
      <SectionTitle>{t("ui.components.activeSubscription.planoAtivo2")}</SectionTitle>

      <p style={{
      margin: "0 0 6px",
      color: "#6f7a79"
    }}>
        {isPaused ? <>{t("ui.components.activeSubscription.oTeuPlano")}<strong>{t("ui.components.activeSubscription.termina")}</strong>{t("ui.components.activeSubscription.a")}{" "}
            <strong>{periodEndLabel}</strong>.
          </> : <>{t("ui.components.activeSubscription.oTeuPlano2")}<strong>{t("ui.components.activeSubscription.renova")}</strong>{t("ui.components.activeSubscription.a2")}{" "}
            <strong>{periodEndLabel}</strong>.
          </>}
      </p>

      {hasScheduledDowngrade && <DowngradeWarning>
          <DowngradeWarningTitle>{t("ui.components.activeSubscription.alteracaoDePlanoAgendada")}</DowngradeWarningTitle>{t("ui.components.activeSubscription.oTeuPlanoVaiMudarPara")}<strong>{downgradeTargetName}</strong>{t("ui.components.activeSubscription.noFimDoPeriodoAtualA")}<strong>{periodEndLabel}</strong>{t("ui.components.activeSubscription.ateEssaDataMantensAcessoAo")}</DowngradeWarning>}

      <PlanName color={color}>{subscription.name || t("ui.i18nFinalCleanup.plan")}</PlanName>

      <PlanDescription>
        {subscription.description || t("ui.i18nFinalCleanup.activePlanDescription", { cadence: isYearly ? t("ui.i18nFinalCleanup.yearly") : t("ui.i18nFinalCleanup.monthly"), status: isPaused ? t("ui.i18nFinalCleanup.paused") : t("ui.i18nFinalCleanup.active") })}
      </PlanDescription>

      {isYearly && <DiscountNote>{t("ui.components.activeSubscription.inclui20DeDescontoEmRelacao")}</DiscountNote>}

      {offers.length > 0 && <>
          <FeatureList iconColor={color}>
            {(showMore ? offers : offers.slice(0, 2)).map((offer, idx) => <li key={idx}>
                <FontAwesomeIcon icon={faCircleCheck} />
                {offer}
              </li>)}
          </FeatureList>

          {offers.length > 2 && <ViewMore color={color} onClick={() => setShowMore(s => !s)}>
              {showMore ? t("ui.screens.choosePlanAndPay.verMenos") : t("ui.screens.choosePlanAndPay.verMais2")}
            </ViewMore>}
        </>}

      <Buttons>
        <Link to={`/pick_plan_and_pay/${restaurant_id}`}>
          <ModernButton color="#99b0ad" fontSize="14px" onClick={onChangePlan}>{t("ui.components.activeSubscription.mudarPlano")}</ModernButton>
        </Link>

        {isPaused ? <ModernButton color="#9FBBC1" fontSize="14px" onClick={askResume}>{t("ui.components.activeSubscription.reativarPlano")}</ModernButton> : <ModernButton color="#ff704d" fontSize="14px" onClick={askCancel}>{t("ui.components.activeSubscription.desativarPlano")}</ModernButton>}
      </Buttons>

      <ConfirmationModal show={confirmOpen} onHide={() => !working && setConfirmOpen(false)} onConfirm={onConfirm} title={confirmType === "cancel" ? t("ui.components.activeSubscription.desativarPlano") : t("ui.components.activeSubscription.reativarPlano")} body={confirmType === "cancel" ? t("ui.i18nFinalCleanup.cancelPlanWarning", { date: periodEndLabel }) : t("ui.i18nCleanup2.oTeuPlanoEstaAtualmenteInativoPretendesReativar")} confirmLabel={working ? t("ui.i18nCleanup2.aProcessar") : confirmType === "cancel" ? t("ui.components.activeSubscription.desativarPlano") : t("ui.components.activeSubscription.reativarPlano")} cancelLabel={working ? t("ui.i18nFinalCleanup.pleaseWait") : t("common.cancel")} confirmColor={confirmType === "cancel" ? "#ff704d" : "#9FBBC1"} cancelColor="#99b0ad" onCancel={() => !working && setConfirmOpen(false)} centered />
    </Wrapper>;
}
